import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { apiFetch, v2, type Submission } from '@/api/v2Client';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { supportsRepWorkingCopy } from '@shared/repEligibility';
import { formatApiError } from '@/lib/errors';

interface WorkingCopy {
  fileName: string;
  content: string;
  missingReviewItems: string[];
  templateVersion: string;
  sourceUrl: string;
}

const buttonStyle: CSSProperties = { padding: '7px 12px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', cursor: 'pointer' };

/** A local working-copy export, never a government submission or final REP XML. */
export function RepWorkingCopyPanel(): ReactElement {
  const permissions = useUserPermissions();
  const allowed = permissions.hasPermission('Documents', 'read') && permissions.hasPermission('Submissions', 'read');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [formType, setFormType] = useState<'RT' | 'PI'>('RT');
  const [workingCopy, setWorkingCopy] = useState<WorkingCopy | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);

  useEffect(() => {
    request.current += 1;
    setWorkingCopy(null);
    setPreparing(false);
    if (!allowed) { setSubmissions([]); setSelectedId(''); return; }
    let cancelled = false;
    setLoading(true);
    setListError(null);
    v2.submissions.list({ region: 'Health Canada', limit: 200 }).then(result => {
      if (cancelled) return;
      setSubmissions(result.data.filter(s => supportsRepWorkingCopy({ ...s })));
      setTotal(result.pagination.total);
    }).catch((reason: unknown) => {
      if (!cancelled) setListError(formatApiError(reason, 'Could not load Canadian submissions.'));
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; request.current += 1; };
  }, [allowed, reload]);

  const prepare = async (): Promise<void> => {
    if (!selectedId || !allowed) return;
    const current = ++request.current;
    setPreparing(true);
    setError(null);
    setWorkingCopy(null);
    try {
      const result = formType === 'PI'
        ? await apiFetch<WorkingCopy>(`/api/v1/submissions/${encodeURIComponent(selectedId)}/rep-pi-working-copy`)
        : await apiFetch<WorkingCopy>(`/api/v1/submissions/${encodeURIComponent(selectedId)}/rep-working-copy`);
      if (current !== request.current) return;
      if (typeof result.fileName !== 'string' || !result.fileName.endsWith('.hcsc') || typeof result.content !== 'string' || !Array.isArray(result.missingReviewItems)) throw new Error('The working-copy response is incomplete. Try preparing it again.');
      setWorkingCopy(result);
    } catch (reason) {
      if (current === request.current) setError(formatApiError(reason, 'Could not prepare the REP working copy.'));
    } finally { if (current === request.current) setPreparing(false); }
  };

  const download = (): void => {
    if (!workingCopy || !allowed) return;
    const url = URL.createObjectURL(new Blob([workingCopy.content], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = workingCopy.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return <section aria-label="Prepare REP working copy" style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 12, lineHeight: 1.5 }}>
    <h3 style={{ margin: '0 0 8px' }}>Prepare a REP working copy</h3>
    <p style={{ margin: '0 0 10px' }}>Choose a transaction (RT) or product information (PI) draft using saved company identifiers, product name and dossier details. Load the downloaded .hcsc in the matching official Health Canada form, complete the remaining details and attestations, validate it there, then generate the final XML.</p>
    <p style={{ fontWeight: 600, margin: '10px 0' }}>The .hcsc file is for editing only. Never include it in a submission.</p>
    {permissions.loading ? <p>Checking access…</p> : permissions.failed ? <p role="alert">Could not check access. <button type="button" onClick={() => void permissions.refresh()} style={buttonStyle}>Retry access check</button></p> : !allowed ? <p>Preparing from saved data requires permission to read submissions and documents. You can still use the official website links above.</p> : <>
      {loading && <p>Loading Canadian submissions…</p>}
      {listError && <p role="alert">{listError} <button type="button" onClick={() => setReload(n => n + 1)} style={buttonStyle}>Retry loading submissions</button></p>}
      {!loading && !listError && <>
        <label style={{ display: 'block', marginBottom: 10 }}>Submission
          <select aria-label="REP submission" value={selectedId} onChange={e => { request.current += 1; setSelectedId(e.target.value); setWorkingCopy(null); setError(null); setPreparing(false); }} style={{ display: 'block', maxWidth: '100%', padding: 8, marginTop: 4 }}>
            <option value="">Choose a Canadian submission</option>
            {submissions.map(s => <option key={s.Id} value={s.Id}>{s.DrugName || 'Unnamed product'} — {s.SubmissionType} — sequence {s.SequenceNumber}</option>)}
          </select>
        </label>
        <label style={{ display: 'block', marginBottom: 10 }}>Working-copy type
          <select aria-label="REP working-copy type" value={formType} onChange={e => {
            request.current += 1;
            setFormType(e.target.value === 'PI' ? 'PI' : 'RT');
            setWorkingCopy(null); setError(null); setPreparing(false);
          }} style={{ display: 'block', maxWidth: '100%', padding: 8, marginTop: 4 }}>
            <option value="RT">Transaction (RT)</option>
            <option value="PI">Product information (PI)</option>
          </select>
        </label>
        {formType === 'PI' && <p style={{ margin: '8px 0' }}>The PI draft starts with identifiers and product name only. Review product details and complete regulatory selections in the official PI form; this draft is not validated XML.</p>}
        {submissions.length === 0 && <p>No submissions with supported human-drug application and regulatory activity types were found. Clinical trials are excluded.</p>}
        {total > 200 && <p>Showing eligible submissions from the first 200 Canadian records. More records exist; this list is not complete.</p>}
        <button type="button" disabled={!selectedId || preparing} onClick={() => void prepare()} style={buttonStyle}>{preparing ? `Preparing ${formType}…` : `Prepare ${formType} working copy`}</button>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0' }}>Only supported regulatory activities can be prepared. No saved data is sent to Health Canada by this action.</p>
      </>}
      {error && <p role="alert">{error} Select a supported submission or retry preparing it.</p>}
      {workingCopy && <div role="status" style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)' }}>
        <p>{formType} working copy prepared for template {workingCopy.templateVersion}. Load it into the official {formType} form and complete its validation before generating final XML.</p>
        {workingCopy.missingReviewItems.length > 0 && <><p style={{ margin: '8px 0' }}>Still needs your review:</p><ul style={{ paddingLeft: 24, margin: '8px 0 12px' }}>{workingCopy.missingReviewItems.map((item, i) => <li key={`${i}-${item}`}>{item}</li>)}</ul></>}
        <button type="button" onClick={download} style={buttonStyle}>Download {formType} working copy (.hcsc)</button>
      </div>}
    </>}
  </section>;
}
